/**
 * Demo-mode fixtures for the analytical + operational endpoints.
 *
 * Keeps the SPA functional without a running FastAPI backend.  Each
 * fixture is deterministic per (dma_id, periodo_id) so the UI can show
 * stable comparativos year-over-year and reproducible KPIs.  Realistic
 * NRW-domain shapes (DMAs, alertas, ordenes, telemetría) drive every
 * operational view.
 */
import type { ApiRequestInit } from "./client";
import { loadRealData, realDataSync, type RealDma } from "./realData";

interface ConsumoSeriePunto {
  ts: string;
  volumen_m3: string;
}

interface DashboardResponse {
  dma_id: string;
  periodo_id: string;
  total_m3: string;
  clientes_activos: number;
  serie: ConsumoSeriePunto[];
}

interface NRWComponentes {
  perdidas_aparentes_m3: string;
  perdidas_reales_m3: string;
}

interface NRWSnapshotResponse {
  dma_id: string | null;
  periodo_id: string;
  nrw_porcentaje: string | null;
  nrw_indefinido_motivo: string | null;
  componentes: NRWComponentes;
  ili?: string | null;
  mnf_lps?: string | null;
}

interface ClienteRead {
  cliente_id: string;
  codigo_externo: string;
  direccion: string;
  estado_servicio: string;
  outside_dma_warning: boolean;
}

interface OrdenInspeccionRead {
  orden_id: string;
  alerta_id: string;
  estado: string;
  resultado: string | null;
  asignada_en: string;
  cerrada_en: string | null;
  sla_dias: number;
  sla_due_at: string;
  sla_progress_pct: number;
  cliente_id?: string;
  cliente_codigo?: string;
  direccion?: string;
  prioridad?: string;
  inspector?: string;
  dma_codigo?: string;
}

export interface DemoDma {
  dma_id: string;
  codigo: string;
  nombre: string;
  clientes: number;
  baseline: number;
  amplitude: number;
  trend: number;
  /** Centroid lon/lat for map operations (Santiago, demo). */
  lon: number;
  lat: number;
  /** Latest NRW % for the home tile (cached). */
  nrw_pct: number;
  ili: number;
  mnf_lps: number;
  pressure_bar: number;
  km_red: number;
  estado_red: "estable" | "vigilancia" | "alerta";
}

const DEMO_DMAS: DemoDma[] = [
  {
    dma_id: "00000000-0000-4000-8000-000000000001",
    codigo: "DMA-001",
    nombre: "Centro",
    clientes: 2_410,
    baseline: 820,
    amplitude: 140,
    trend: 4.5,
    lon: -70.6483,
    lat: -33.4378,
    nrw_pct: 24.2,
    ili: 1.6,
    mnf_lps: 4.2,
    pressure_bar: 3.8,
    km_red: 28.4,
    estado_red: "vigilancia",
  },
  {
    dma_id: "00000000-0000-4000-8000-000000000002",
    codigo: "DMA-002",
    nombre: "Norte",
    clientes: 1_960,
    baseline: 660,
    amplitude: 90,
    trend: 2.0,
    lon: -70.6342,
    lat: -33.4187,
    nrw_pct: 19.8,
    ili: 1.2,
    mnf_lps: 2.6,
    pressure_bar: 4.1,
    km_red: 19.7,
    estado_red: "estable",
  },
  {
    dma_id: "00000000-0000-4000-8000-000000000003",
    codigo: "DMA-003",
    nombre: "Sur",
    clientes: 3_180,
    baseline: 980,
    amplitude: 200,
    trend: 6.2,
    lon: -70.6601,
    lat: -33.4626,
    nrw_pct: 36.1,
    ili: 2.9,
    mnf_lps: 7.8,
    pressure_bar: 3.2,
    km_red: 41.0,
    estado_red: "alerta",
  },
  {
    dma_id: "00000000-0000-4000-8000-000000000004",
    codigo: "DMA-004",
    nombre: "Oriente",
    clientes: 2_240,
    baseline: 740,
    amplitude: 110,
    trend: 3.5,
    lon: -70.5742,
    lat: -33.4196,
    nrw_pct: 22.7,
    ili: 1.4,
    mnf_lps: 3.4,
    pressure_bar: 4.0,
    km_red: 24.8,
    estado_red: "estable",
  },
  {
    dma_id: "00000000-0000-4000-8000-000000000005",
    codigo: "DMA-005",
    nombre: "Poniente",
    clientes: 2_810,
    baseline: 870,
    amplitude: 160,
    trend: 5.0,
    lon: -70.7104,
    lat: -33.4351,
    nrw_pct: 31.4,
    ili: 2.4,
    mnf_lps: 6.1,
    pressure_bar: 3.5,
    km_red: 33.2,
    estado_red: "alerta",
  },
];

/** Deriva una DemoDma desde una DMA real (coordenadas Antofagasta). */
function realToDemoDma(d: RealDma, idx: number): DemoDma {
  const baseline = Math.max(200, Math.round(d.volumen_total / Math.max(1, d.clientes) / 1000));
  return {
    dma_id: `00000000-0000-4000-8000-${(1 + idx).toString().padStart(12, "0")}`,
    codigo: d.codigo,
    nombre: d.nombre,
    clientes: d.clientes,
    baseline,
    amplitude: Math.round(baseline * 0.18),
    trend: 2 + (d.nrw_pct - 20) * 0.2,
    lon: d.lon,
    lat: d.lat,
    nrw_pct: d.nrw_pct,
    // ILI y MNF derivados del NRW real (no vienen en el parquet).
    ili: Number((0.8 + (d.nrw_pct - 18) * 0.12).toFixed(2)),
    mnf_lps: Number((2 + (d.nrw_pct - 18) * 0.35).toFixed(1)),
    pressure_bar: Number((4.2 - (d.nrw_pct - 18) * 0.04).toFixed(1)),
    km_red: Number((d.clientes / 90).toFixed(1)),
    estado_red: d.nrw_pct >= 30 ? "alerta" : d.nrw_pct >= 25 ? "vigilancia" : "estable",
  };
}

export function listDemoDmas(): DemoDma[] {
  // Si hay datos reales cargados, derivar las DMAs de Antofagasta.
  const real = realDataSync();
  if (real && real.dmas.length > 0) {
    return real.dmas.map(realToDemoDma);
  }
  return DEMO_DMAS;
}

function daysInPeriodo(periodoId: string): number {
  const month = Number(periodoId.split("-")[1] ?? "01");
  const year = Number(periodoId.split("-")[0] ?? "2025");
  return new Date(year, month, 0).getDate();
}

function buildDashboard(dmaId: string, periodoId: string): DashboardResponse {
  const dma =
    DEMO_DMAS.find((d) => d.dma_id === dmaId) ?? {
      ...DEMO_DMAS[0],
      dma_id: dmaId,
    };
  const month = periodoId.split("-")[1] ?? "01";
  const year = periodoId.split("-")[0] ?? "2025";
  const monthIdx = Number(month);
  const yearIdx = Number(year);
  const seasonal = 1 + Math.sin(((monthIdx - 1) / 12) * Math.PI * 2) * 0.08;
  const yearDrift = (yearIdx - 2024) * dma.trend * 4;
  const days = daysInPeriodo(periodoId);
  const serie: ConsumoSeriePunto[] = [];
  let total = 0;
  for (let day = 1; day <= days; day += 1) {
    const noise = Math.sin(day / 2.5) * dma.amplitude * 0.4;
    const trend = day * dma.trend * 0.35;
    const value = Math.max(
      0,
      Math.round((dma.baseline + yearDrift + trend + noise) * seasonal),
    );
    total += value;
    const dateStr = `${year}-${month}-${day
      .toString()
      .padStart(2, "0")}T00:00:00+00:00`;
    serie.push({ ts: dateStr, volumen_m3: value.toFixed(3) });
  }
  return {
    dma_id: dmaId,
    periodo_id: periodoId,
    total_m3: total.toFixed(3),
    clientes_activos: dma.clientes,
    serie,
  };
}

function buildNrwList(periodoId: string): NRWSnapshotResponse[] {
  const dmas = listDemoDmas();
  const totalClientes = dmas.reduce((a, d) => a + d.clientes, 0) || 1;
  const nrwRed = dmas.reduce((a, d) => a + d.nrw_pct * d.clientes, 0) / totalClientes;
  return [
    {
      dma_id: null,
      periodo_id: periodoId,
      nrw_porcentaje: nrwRed.toFixed(6),
      nrw_indefinido_motivo: null,
      componentes: {
        perdidas_aparentes_m3: (dmas.length * 1200 + nrwRed * 320).toFixed(3),
        perdidas_reales_m3: (dmas.length * 2400 + nrwRed * 640).toFixed(3),
      },
      ili: (nrwRed * 0.07).toFixed(2),
      mnf_lps: (nrwRed * 0.9).toFixed(2),
    },
    ...dmas.map((dma) => ({
      dma_id: dma.dma_id,
      periodo_id: periodoId,
      nrw_porcentaje: dma.nrw_pct.toFixed(6),
      nrw_indefinido_motivo: null,
      componentes: {
        perdidas_aparentes_m3: (3000 + dma.nrw_pct * 80).toFixed(3),
        perdidas_reales_m3: (5500 + dma.nrw_pct * 220).toFixed(3),
      },
      ili: dma.ili.toFixed(2),
      mnf_lps: dma.mnf_lps.toFixed(2),
    })),
  ];
}

function buildClientes(): ClienteRead[] {
  return Array.from({ length: 24 }, (_, idx) => ({
    cliente_id: `00000000-0000-4000-8000-${(100 + idx)
      .toString()
      .padStart(12, "0")}`,
    codigo_externo: `CLI-${(1000 + idx).toString()}`,
    direccion: `Av. Demo ${idx + 1}`,
    estado_servicio: idx % 7 === 0 ? "suspendido" : "activo",
    outside_dma_warning: idx % 11 === 0,
  }));
}

// ---------------------------------------------------------------------------
// Alertas inteligentes (Centro de Alertas)
// ---------------------------------------------------------------------------

export type AlertaSeveridad = "critica" | "alta" | "media" | "baja";

export interface AlertaIntel {
  alerta_id: string;
  tipo: string;
  tipo_label: string;
  severidad: AlertaSeveridad;
  score: number;
  estado: "nueva" | "asignada" | "en_inspeccion" | "cerrada";
  cliente_codigo: string;
  cliente_id: string;
  dma_codigo: string;
  dma_id: string;
  direccion: string;
  detectado_en: string;
  due_at: string;
  sla_progress_pct: number;
  volumen_estimado_m3: number;
  inspector: string | null;
  components: Array<{ key: string; label: string; weight: number; value: string }>;
  evidencia: {
    serie: number[];
    umbral?: number;
    promedio_dma?: number;
    presion_setpoint_bar?: number;
    presion_observada_bar?: number;
  };
  geo: { lon: number; lat: number };
  incidente_id?: string;
  reincidencia_count: number;
}

const ALERTA_TIPOS: Array<{ key: string; label: string; sev: AlertaSeveridad; weight: Record<string, number> }> = [
  {
    key: "fuga",
    label: "Fuga probable",
    sev: "critica",
    weight: {
      probabilidad_modelo: 0.32,
      impacto_economico: 0.28,
      reincidencia: 0.12,
      presion: 0.15,
      antiguedad_infra: 0.13,
    },
  },
  {
    key: "fraude",
    label: "Sospecha de fraude",
    sev: "alta",
    weight: {
      patron_anomalo: 0.34,
      probabilidad_modelo: 0.22,
      reincidencia: 0.18,
      reclamos_cercanos: 0.13,
      antiguedad_medidor: 0.13,
    },
  },
  {
    key: "consumo_anomalo",
    label: "Consumo anómalo",
    sev: "media",
    weight: {
      desviacion_ma90: 0.4,
      probabilidad_modelo: 0.25,
      reincidencia: 0.15,
      impacto_economico: 0.2,
    },
  },
  {
    key: "flujo_inverso",
    label: "Flujo inverso",
    sev: "alta",
    weight: {
      delta_acumulado: 0.45,
      presion: 0.2,
      proximidad_valvula: 0.15,
      antiguedad_medidor: 0.2,
    },
  },
  {
    key: "medidor_detenido",
    label: "Medidor detenido",
    sev: "alta",
    weight: {
      gap_lecturas: 0.4,
      probabilidad_modelo: 0.3,
      antiguedad_medidor: 0.3,
    },
  },
  {
    key: "medidor_manipulado",
    label: "Medidor manipulado",
    sev: "critica",
    weight: {
      rtc_drift: 0.35,
      probabilidad_modelo: 0.3,
      reincidencia: 0.2,
      alarma_firmware: 0.15,
    },
  },
  {
    key: "dma_desbalanceado",
    label: "DMA desbalanceado",
    sev: "critica",
    weight: {
      vol_in_minus_out: 0.5,
      ili: 0.2,
      mnf: 0.2,
      tendencia: 0.1,
    },
  },
  {
    key: "perdida_nocturna",
    label: "Pérdida nocturna",
    sev: "alta",
    weight: {
      mnf_delta: 0.45,
      probabilidad_modelo: 0.25,
      presion: 0.15,
      reincidencia: 0.15,
    },
  },
  {
    key: "presion_anomala",
    label: "Presión anómala",
    sev: "alta",
    weight: {
      delta_setpoint: 0.5,
      transiente: 0.2,
      reincidencia: 0.15,
      antiguedad_infra: 0.15,
    },
  },
];

function rng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0xffffffff;
  };
}

function buildAlertasIntel(): AlertaIntel[] {
  const out: AlertaIntel[] = [];
  const r = rng(42);
  const baseDate = new Date("2025-06-04T12:00:00Z").getTime();

  for (let idx = 0; idx < 24; idx += 1) {
    const tipo = ALERTA_TIPOS[idx % ALERTA_TIPOS.length];
    const dma = DEMO_DMAS[idx % DEMO_DMAS.length];
    const score = Math.round(35 + r() * 60);
    const sev: AlertaSeveridad =
      score >= 85
        ? "critica"
        : score >= 65
          ? "alta"
          : score >= 45
            ? "media"
            : "baja";
    const detected = baseDate - idx * 3_600_000 - Math.floor(r() * 7_200_000);
    const slaHours = sev === "critica" ? 8 : sev === "alta" ? 24 : 72;
    const due = detected + slaHours * 3_600_000;
    const elapsed = baseDate - detected;
    const slaProgress = Math.min(100, Math.round((elapsed / (slaHours * 3_600_000)) * 100));
    const components = Object.entries(tipo.weight).map(([key, weight]) => ({
      key,
      label: key.replace(/_/g, " "),
      weight,
      value: (50 + r() * 50).toFixed(1),
    }));

    const serie = Array.from({ length: 30 }, (_, i) => {
      const base = dma.baseline / 30 + r() * dma.amplitude * 0.05;
      if (tipo.key === "alza_brusca" && i > 22) return base * (2 + r());
      if (tipo.key === "caida_brusca" && i > 22) return base * 0.2;
      if (tipo.key === "consumo_cero_sostenido" && i > 18) return 0;
      return Math.max(0, base + (r() - 0.5) * dma.amplitude * 0.1);
    });

    const inspector = idx % 3 === 0 ? null : ["Pérez", "Soto", "Muñoz", "Rivera"][idx % 4];
    const estado: AlertaIntel["estado"] =
      idx < 6 ? "nueva" : idx < 12 ? "asignada" : idx < 20 ? "en_inspeccion" : "cerrada";

    const lonJitter = (r() - 0.5) * 0.012;
    const latJitter = (r() - 0.5) * 0.012;

    const incidenteId = idx < 4 ? "incidente-fuga-norte" : idx < 8 ? "incidente-mnf-sur" : undefined;

    out.push({
      alerta_id: `00000000-0000-4000-8000-${(700 + idx).toString().padStart(12, "0")}`,
      tipo: tipo.key,
      tipo_label: tipo.label,
      severidad: sev,
      score,
      estado,
      cliente_codigo: `CLI-${(2000 + idx).toString()}`,
      cliente_id: `00000000-0000-4000-8000-${(2000 + idx).toString().padStart(12, "0")}`,
      dma_codigo: dma.codigo,
      dma_id: dma.dma_id,
      direccion: `Calle Demo ${100 + idx}, ${dma.nombre}`,
      detectado_en: new Date(detected).toISOString(),
      due_at: new Date(due).toISOString(),
      sla_progress_pct: slaProgress,
      volumen_estimado_m3: Math.round(40 + r() * 1200),
      inspector,
      components,
      evidencia: {
        serie,
        umbral: dma.baseline / 30,
        promedio_dma: dma.baseline / 30,
        presion_setpoint_bar: dma.pressure_bar,
        presion_observada_bar: dma.pressure_bar + (r() - 0.5) * 1.5,
      },
      geo: { lon: dma.lon + lonJitter, lat: dma.lat + latJitter },
      incidente_id: incidenteId,
      reincidencia_count: idx % 4,
    });
  }
  return out;
}

const ALERTAS_DEMO = buildAlertasIntel();

// ---------------------------------------------------------------------------
// Órdenes (extendidas)
// ---------------------------------------------------------------------------

function buildOrdenes(): OrdenInspeccionRead[] {
  const base = new Date("2025-06-01T12:00:00Z");
  return Array.from({ length: 12 }, (_, idx) => {
    const asignada = new Date(base.getTime() - idx * 86_400_000);
    const slaDias = idx === 0 ? 1 : idx % 3 === 0 ? 3 : 10;
    const due = new Date(asignada.getTime() + slaDias * 86_400_000);
    const elapsed = base.getTime() - asignada.getTime();
    const slaProgress = Math.min(100, Math.round((elapsed / (slaDias * 86_400_000)) * 100));
    const dma = DEMO_DMAS[idx % DEMO_DMAS.length];
    return {
      orden_id: `00000000-0000-4000-8000-${(900 + idx).toString().padStart(12, "0")}`,
      alerta_id: ALERTAS_DEMO[idx % ALERTAS_DEMO.length]?.alerta_id ?? "—",
      estado: idx === 0 ? "asignada" : idx < 5 ? "en_inspeccion" : "cerrada",
      resultado: idx >= 5 ? (idx === 5 ? "regularizacion" : idx === 6 ? "corte" : "sin_hallazgos") : null,
      asignada_en: asignada.toISOString(),
      cerrada_en: idx >= 5 ? new Date(asignada.getTime() + 2 * 86_400_000).toISOString() : null,
      sla_dias: slaDias,
      sla_due_at: due.toISOString(),
      sla_progress_pct: slaProgress,
      cliente_codigo: `CLI-${(2000 + idx).toString()}`,
      cliente_id: `00000000-0000-4000-8000-${(2000 + idx).toString().padStart(12, "0")}`,
      direccion: `Calle Demo ${100 + idx}, ${dma.nombre}`,
      prioridad: idx === 0 ? "crítica" : idx < 4 ? "alta" : "media",
      inspector: ["Pérez", "Soto", "Muñoz"][idx % 3],
      dma_codigo: dma.codigo,
    };
  });
}

// ---------------------------------------------------------------------------
// KPIs ejecutivos
// ---------------------------------------------------------------------------

interface ExecutiveKpis {
  nrw_red_pct: number;
  nrw_red_delta_pp: number;
  volumen_recuperable_m3: number;
  perdida_evitada_clp: number;
  roi_factor: number;
  eficiencia_cuadrillas_pct: number;
  cumplimiento_sla_pct: number;
  cobertura_ami_pct: number;
  tiempo_medio_cierre_h: number;
  alertas_abiertas: number;
  alertas_criticas: number;
  ranking: Array<{ dma_codigo: string; nrw_pct: number; ili: number; mnf_lps: number }>;
  trend_nrw_red: number[];
  trend_recuperable: number[];
}

function buildExecutiveKpis(): ExecutiveKpis {
  const dmas = listDemoDmas();
  const real = realDataSync();
  const TARIFA_M3 = 950; // CLP demo

  const ranking = dmas
    .map((d) => ({ dma_codigo: d.codigo, nrw_pct: d.nrw_pct, ili: d.ili, mnf_lps: d.mnf_lps }))
    .sort((a, b) => b.nrw_pct - a.nrw_pct);

  // NRW de red ponderado por clientes.
  const totalClientes = dmas.reduce((a, d) => a + d.clientes, 0) || 1;
  const nrwRed = Number(
    (dmas.reduce((a, d) => a + d.nrw_pct * d.clientes, 0) / totalClientes).toFixed(2),
  );

  const recuperable = ALERTAS_DEMO.filter((a) => a.estado !== "cerrada").reduce(
    (acc, a) => acc + a.volumen_estimado_m3,
    0,
  );

  // Con datos reales: cobertura AMI y alertas críticas desde alarmas reales.
  const coberturaAmi = real ? 97.8 : 96.4;
  const alertasCriticasReal = real
    ? real.alarmas.filter((a) => a.alarm_burst || a.alarm_manipulation).length
    : ALERTAS_DEMO.filter((a) => a.estado !== "cerrada" && a.severidad === "critica").length;
  const alertasAbiertasReal = real
    ? real.alarmas.length
    : ALERTAS_DEMO.filter((a) => a.estado !== "cerrada").length;

  return {
    nrw_red_pct: nrwRed,
    nrw_red_delta_pp: -1.6,
    volumen_recuperable_m3: recuperable,
    perdida_evitada_clp: Math.round(recuperable * TARIFA_M3 * 0.62),
    roi_factor: 4.2,
    eficiencia_cuadrillas_pct: 78,
    cumplimiento_sla_pct: 91,
    cobertura_ami_pct: coberturaAmi,
    tiempo_medio_cierre_h: 38,
    alertas_abiertas: alertasAbiertasReal,
    alertas_criticas: alertasCriticasReal,
    ranking,
    trend_nrw_red: [nrwRed + 2.8, nrwRed + 2.3, nrwRed + 1.5, nrwRed + 1.1, nrwRed + 0.4, nrwRed].map(
      (v) => Number(v.toFixed(2)),
    ),
    trend_recuperable: [3800, 4120, 4450, 4720, 4920, recuperable],
  };
}

// ---------------------------------------------------------------------------
// GIS / mapa operacional
// ---------------------------------------------------------------------------

interface GisFeature {
  id: string;
  kind: "alerta" | "orden" | "medidor" | "cliente" | "valvula" | "gateway";
  lon: number;
  lat: number;
  meta: Record<string, string | number>;
}

function buildGisFeatures(): GisFeature[] {
  const out: GisFeature[] = [];
  for (const a of ALERTAS_DEMO) {
    out.push({
      id: a.alerta_id,
      kind: "alerta",
      lon: a.geo.lon,
      lat: a.geo.lat,
      meta: {
        tipo: a.tipo_label,
        severidad: a.severidad,
        score: a.score,
        dma: a.dma_codigo,
        sla_pct: a.sla_progress_pct,
      },
    });
  }
  // Decorate with random meters / clients / valves around each DMA
  const r = rng(7);
  for (const dma of DEMO_DMAS) {
    for (let i = 0; i < 10; i += 1) {
      const lon = dma.lon + (r() - 0.5) * 0.022;
      const lat = dma.lat + (r() - 0.5) * 0.022;
      out.push({
        id: `${dma.codigo}-medidor-${i}`,
        kind: "medidor",
        lon,
        lat,
        meta: { dma: dma.codigo, serial: `M-${dma.codigo}-${i}` },
      });
    }
    out.push({
      id: `${dma.codigo}-gw`,
      kind: "gateway",
      lon: dma.lon + 0.005,
      lat: dma.lat + 0.005,
      meta: { dma: dma.codigo, cobertura_m: 1200 },
    });
    out.push({
      id: `${dma.codigo}-valv`,
      kind: "valvula",
      lon: dma.lon - 0.005,
      lat: dma.lat - 0.005,
      meta: { dma: dma.codigo, estado: "abierta" },
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Calidad de datos
// ---------------------------------------------------------------------------

interface DqMetrics {
  cobertura_lecturas_pct: number;
  duplicados: number;
  rtc_drift: number;
  gaps: number;
  timezone_issues: number;
  lecturas_imposibles: number;
  por_dma: Array<{ dma_codigo: string; cobertura_pct: number; gaps: number; alertas: number }>;
  reglas: Array<{ codigo: string; descripcion: string; impacto: string; eventos_24h: number }>;
}

function buildDqMetrics(): DqMetrics {
  return {
    cobertura_lecturas_pct: 96.4,
    duplicados: 24,
    rtc_drift: 7,
    gaps: 41,
    timezone_issues: 2,
    lecturas_imposibles: 5,
    por_dma: DEMO_DMAS.map((d) => ({
      dma_codigo: d.codigo,
      cobertura_pct: 90 + Math.round((d.nrw_pct < 25 ? 8 : 4)),
      gaps: Math.round(d.nrw_pct / 4),
      alertas: ALERTAS_DEMO.filter((a) => a.dma_codigo === d.codigo).length,
    })),
    reglas: [
      { codigo: "DUPLICATE", descripcion: "Lecturas duplicadas", impacto: "bajo", eventos_24h: 24 },
      { codigo: "RTC_DRIFT", descripcion: "Reloj del medidor con drift > 5 min", impacto: "medio", eventos_24h: 7 },
      { codigo: "GAP_DETECTED", descripcion: "Hueco temporal en serie", impacto: "alto", eventos_24h: 41 },
      { codigo: "IMPOSSIBLE_DELTA", descripcion: "Delta físicamente imposible", impacto: "alto", eventos_24h: 5 },
      { codigo: "TIMEZONE_MISMATCH", descripcion: "Timezone inconsistente", impacto: "medio", eventos_24h: 2 },
    ],
  };
}

// ---------------------------------------------------------------------------
// Casos Prioritarios — Operational Priority Score (módulo estrella)
// ---------------------------------------------------------------------------

export interface PriorityCase {
  caso_id: string;
  ops_score: number;
  prioridad: "P1" | "P2" | "P3" | "P4";
  tipo: string;
  tipo_label: string;
  titulo: string;
  dma_codigo: string;
  dma_id: string;
  cliente_codigo: string | null;
  direccion: string;
  /** Económico */
  perdida_estimada_m3_dia: number;
  perdida_estimada_clp_mes: number;
  costo_intervencion_clp: number;
  roi_factor: number;
  payback_dias: number;
  prob_recuperacion: number;
  /** Variables del score */
  factores: Array<{ key: string; label: string; weight: number; valor: number; contrib: number }>;
  /** Explainability con signo: ±puntos que suben o bajan el score. */
  explain: Array<{ label: string; puntos: number; fuente: string }>;
  /** Timeline causal de eventos que construyeron el caso. */
  timeline: Array<{ ts: string; tipo: string; texto: string; severidad: AlertaSeveridad }>;
  /** Grafo de entidades relacionadas. */
  entidades: Array<{ tipo: string; ref: string; rel: string; to: string }>;
  /** Órdenes asociadas. */
  ordenes_rel: Array<{ orden_id: string; estado: string }>;
  /** Razonamiento del motor IA (narrativa). */
  ai_reasoning: string;
  recomendacion: string;
  accion_sugerida: string;
  eta_horas: number;
  sla_horas: number;
  sla_progress_pct: number;
  confianza_modelo: number;
  estado: "abierto" | "asignado" | "en_progreso" | "resuelto";
  cuadrilla_sugerida: string;
  geo: { lon: number; lat: number };
  tendencia: number[];
}

const CASO_TIPOS: Array<{
  key: string;
  label: string;
  factores: Record<string, number>;
  recomendacion: string;
  accion: string;
}> = [
  {
    key: "fuga_troncal",
    label: "Fuga en matriz troncal",
    factores: {
      perdida_estimada: 0.3,
      mnf: 0.2,
      presion: 0.15,
      edad_infra: 0.15,
      reincidencia: 0.1,
      prob_modelo: 0.1,
    },
    recomendacion: "Inspección prioritaria con geófono + correlador acústico en el tramo.",
    accion: "Despachar cuadrilla de detección acústica",
  },
  {
    key: "fraude_comercial",
    label: "Fraude / by-pass comercial",
    factores: {
      prob_fraude: 0.32,
      perdida_estimada: 0.24,
      reincidencia: 0.18,
      criticidad_cliente: 0.14,
      anomalias: 0.12,
    },
    recomendacion: "Inspección encubierta + revisión de empalme y medidor.",
    accion: "Asignar inspector de fraude",
  },
  {
    key: "medidor_falla",
    label: "Medidor con falla probable",
    factores: {
      prob_falla: 0.34,
      edad_infra: 0.24,
      perdida_estimada: 0.2,
      anomalias: 0.12,
      reincidencia: 0.1,
    },
    recomendacion: "Reemplazo preventivo de medidor antes de sub-registro mayor.",
    accion: "Generar orden de reemplazo",
  },
  {
    key: "dma_empeorando",
    label: "DMA empeorando (tendencia)",
    factores: {
      tendencia_nrw: 0.3,
      mnf: 0.22,
      perdida_estimada: 0.2,
      presion: 0.16,
      anomalias: 0.12,
    },
    recomendacion: "Sectorizar y barrer la DMA por la noche (step-test de MNF).",
    accion: "Programar step-test nocturno",
  },
  {
    key: "perdida_nocturna",
    label: "Pérdida nocturna elevada",
    factores: {
      mnf: 0.34,
      perdida_estimada: 0.24,
      presion: 0.18,
      prob_modelo: 0.14,
      reincidencia: 0.1,
    },
    recomendacion: "Bajar presión nocturna vía PRV y confirmar reducción de MNF.",
    accion: "Ajustar setpoint PRV nocturno",
  },
];

function buildPriorityCases(): PriorityCase[] {
  const out: PriorityCase[] = [];
  const r = rng(101);
  const TARIFA = 950;
  for (let i = 0; i < 14; i += 1) {
    const tipo = CASO_TIPOS[i % CASO_TIPOS.length];
    const dma = DEMO_DMAS[i % DEMO_DMAS.length];
    const perdidaDia = Math.round(20 + r() * 380);
    const perdidaMes = perdidaDia * 30;
    const perdidaClp = perdidaMes * TARIFA;
    const costo = Math.round(180_000 + r() * 1_400_000);
    const probRec = 0.45 + r() * 0.5;
    const recuperableClp = perdidaClp * probRec;
    const roi = recuperableClp / costo;
    const payback = Math.max(1, Math.round(costo / (recuperableClp / 30)));

    // Build weighted factors → ops_score (0..100)
    let scoreAcc = 0;
    const factores = Object.entries(tipo.factores).map(([key, weight]) => {
      const valor = Math.round(35 + r() * 65);
      const contrib = valor * weight;
      scoreAcc += contrib;
      return { key, label: key.replace(/_/g, " "), weight, valor, contrib: Math.round(contrib) };
    });
    const opsScore = Math.min(100, Math.round(scoreAcc));
    const prioridad: PriorityCase["prioridad"] =
      opsScore >= 80 ? "P1" : opsScore >= 65 ? "P2" : opsScore >= 50 ? "P3" : "P4";
    const slaHoras = prioridad === "P1" ? 8 : prioridad === "P2" ? 24 : prioridad === "P3" ? 72 : 168;
    const etaHoras = Math.round(2 + r() * (slaHoras * 0.6));
    const slaProgress = Math.round((etaHoras / slaHoras) * 100);
    const estado: PriorityCase["estado"] =
      i < 5 ? "abierto" : i < 9 ? "asignado" : i < 12 ? "en_progreso" : "resuelto";

    // Explainability con signo — qué subió y qué bajó el score.
    const explainPool: Array<{ label: string; puntos: number; fuente: string }> = [
      { label: "MNF nocturno elevado (+18% en 72h)", puntos: 14, fuente: "telemetría DMA" },
      { label: "Presión nocturna sobre setpoint", puntos: 9, fuente: "SCADA / PRV" },
      { label: "Fuga histórica cercana (<120m)", puntos: 6, fuente: "grafo GIS" },
      { label: "Reincidencia del cliente", puntos: 5, fuente: "histórico casos" },
      { label: "Edad de infraestructura > 30 años", puntos: 4, fuente: "catastro red" },
      { label: "Cobertura RF degradada en sector", puntos: -3, fuente: "observabilidad IoT" },
      { label: "Datos con gaps recientes", puntos: -2, fuente: "calidad de datos" },
    ];
    // Pick a deterministic subset per case.
    const explain = explainPool.filter((_, k) => (i + k) % 2 === 0 || k < 3).slice(0, 5);

    const now = new Date("2025-06-04T12:00:00Z").getTime();
    const mkts = (h: number) => new Date(now - h * 3_600_000).toISOString();
    const timeline: PriorityCase["timeline"] = [
      { ts: mkts(72), tipo: "anomalia", texto: `MNF de ${dma.codigo} comienza a subir sobre línea base`, severidad: "media" },
      { ts: mkts(48), tipo: "correlacion", texto: "Sin aumento de demanda diurna → descarta mayor consumo", severidad: "media" },
      { ts: mkts(24), tipo: "anomalia", texto: "Tercera noche consecutiva de MNF elevado", severidad: "alta" },
      { ts: mkts(6), tipo: "scoring", texto: `Motor eleva OPS score a ${opsScore} (${prioridad})`, severidad: opsScore >= 80 ? "critica" : "alta" },
      { ts: mkts(1), tipo: "recomendacion", texto: tipo.recomendacion, severidad: "alta" },
    ];

    const entidades: PriorityCase["entidades"] = [
      { tipo: "DMA", ref: dma.codigo, rel: "ubicado en", to: `/mapa?dma=${dma.codigo}` },
      { tipo: "Gateway", ref: `GW-${dma.codigo}`, rel: "telemetría vía", to: "/observabilidad" },
      { tipo: "Predicción", ref: "leak-xgb-v4", rel: "evaluado por", to: "/prediccion" },
      { tipo: "Cuadrilla", ref: ["Cuadrilla Norte", "Cuadrilla Sur", "Detección Acústica", "Fraude"][i % 4], rel: "sugerida", to: "/cuadrillas" },
    ];

    const ordenesRel: PriorityCase["ordenes_rel"] =
      estado === "abierto"
        ? []
        : [{ orden_id: `OR-${900 + i}`, estado: estado === "resuelto" ? "cerrada" : "en_inspeccion" }];

    const aiReasoning =
      `El caso se eleva a ${prioridad} porque el patrón temporal (MNF creciente por 3 noches sin ` +
      `cambio de demanda) es altamente compatible con una ${tipo.label.toLowerCase()}. ` +
      `La señal de telemetría aporta la mayor parte del score; la cobertura RF degradada resta ` +
      `confianza, por lo que se recomienda validar en terreno antes de intervención mayor. ` +
      `Impacto económico estimado en CLP ${(Math.round(perdidaClp) / 1_000_000).toFixed(1)}M/mes ` +
      `con probabilidad de recuperación ${Math.round(probRec * 100)}%.`;

    out.push({
      caso_id: `CASO-${(1000 + i).toString()}`,
      ops_score: opsScore,
      prioridad,
      tipo: tipo.key,
      tipo_label: tipo.label,
      titulo: `${tipo.label} · ${dma.codigo}`,
      dma_codigo: dma.codigo,
      dma_id: dma.dma_id,
      cliente_codigo: tipo.key === "dma_empeorando" ? null : `CLI-${(3000 + i).toString()}`,
      direccion: `Sector ${dma.nombre}, eje ${100 + i}`,
      perdida_estimada_m3_dia: perdidaDia,
      perdida_estimada_clp_mes: Math.round(perdidaClp),
      costo_intervencion_clp: costo,
      roi_factor: Number(roi.toFixed(1)),
      payback_dias: payback,
      prob_recuperacion: Number(probRec.toFixed(2)),
      factores,
      explain,
      timeline,
      entidades,
      ordenes_rel: ordenesRel,
      ai_reasoning: aiReasoning,
      recomendacion: tipo.recomendacion,
      accion_sugerida: tipo.accion,
      eta_horas: etaHoras,
      sla_horas: slaHoras,
      sla_progress_pct: slaProgress,
      confianza_modelo: Number((0.62 + r() * 0.36).toFixed(2)),
      estado,
      cuadrilla_sugerida: ["Cuadrilla Norte", "Cuadrilla Sur", "Detección Acústica", "Fraude"][i % 4],
      geo: { lon: dma.lon + (r() - 0.5) * 0.01, lat: dma.lat + (r() - 0.5) * 0.01 },
      tendencia: Array.from({ length: 12 }, (_, t) => perdidaDia * (0.7 + t * 0.03 + r() * 0.1)),
    });
    void recuperableClp;
  }
  out.sort((a, b) => b.ops_score - a.ops_score);
  return out;
}

const CASOS_DEMO = buildPriorityCases();

// ---------------------------------------------------------------------------
// Cuadrillas
// ---------------------------------------------------------------------------

interface Cuadrilla {
  cuadrilla_id: string;
  nombre: string;
  tipo: string;
  estado: "disponible" | "en_ruta" | "en_sitio" | "fuera_turno";
  miembros: number;
  ordenes_activas: number;
  ordenes_cerradas_hoy: number;
  eficiencia_pct: number;
  zona: string;
  lat: number;
  lon: number;
}

function buildCuadrillas(): Cuadrilla[] {
  const nombres = ["Cuadrilla Norte", "Cuadrilla Sur", "Detección Acústica", "Fraude", "Reemplazo Medidores"];
  const r = rng(55);
  return nombres.map((nombre, i) => {
    const dma = DEMO_DMAS[i % DEMO_DMAS.length];
    return {
      cuadrilla_id: `CUAD-${(10 + i).toString()}`,
      nombre,
      tipo: i === 2 ? "deteccion" : i === 3 ? "fraude" : i === 4 ? "reemplazo" : "general",
      estado: (["disponible", "en_ruta", "en_sitio", "disponible", "fuera_turno"] as const)[i % 5],
      miembros: 2 + (i % 3),
      ordenes_activas: Math.round(r() * 5),
      ordenes_cerradas_hoy: Math.round(r() * 8),
      eficiencia_pct: Math.round(68 + r() * 28),
      zona: dma.nombre,
      lat: dma.lat,
      lon: dma.lon,
    };
  });
}

// ---------------------------------------------------------------------------
// Observabilidad IoT (gateways, RSSI, baterías, pipelines)
// ---------------------------------------------------------------------------

interface Observability {
  gateways: Array<{
    gateway_id: string;
    dma_codigo: string;
    estado: "online" | "degradado" | "offline";
    rssi_dbm: number;
    snr_db: number;
    uptime_pct: number;
    paquetes_perdidos_pct: number;
    firmware: string;
    medidores: number;
  }>;
  baterias: { criticas: number; bajas: number; ok: number };
  ingestion: {
    lecturas_hora: number;
    lag_segundos: number;
    backlog: number;
    tasa_error_pct: number;
    pipelines: Array<{ nombre: string; estado: string; p95_ms: number }>;
  };
}

function buildObservability(): Observability {
  const r = rng(77);
  return {
    gateways: DEMO_DMAS.map((d, i) => {
      const estado: "online" | "degradado" | "offline" =
        i === 2 ? "degradado" : i === 4 ? "offline" : "online";
      return {
        gateway_id: `GW-${d.codigo}`,
        dma_codigo: d.codigo,
        estado,
        rssi_dbm: estado === "offline" ? -120 : Math.round(-70 - r() * 35),
        snr_db: estado === "offline" ? 0 : Number((4 + r() * 8).toFixed(1)),
        uptime_pct: estado === "offline" ? 0 : Number((97 + r() * 2.8).toFixed(2)),
        paquetes_perdidos_pct: estado === "offline" ? 100 : Number((r() * 6).toFixed(1)),
        firmware: `v2.${4 + (i % 3)}.1`,
        medidores: Math.round(300 + r() * 900),
      };
    }),
    baterias: { criticas: 18, bajas: 64, ok: 9120 },
    ingestion: {
      lecturas_hora: 284_500,
      lag_segundos: 4.2,
      backlog: 1280,
      tasa_error_pct: 0.4,
      pipelines: [
        { nombre: "lecturas.raw → silver", estado: "healthy", p95_ms: 180 },
        { nombre: "silver → nrw.gold", estado: "healthy", p95_ms: 420 },
        { nombre: "anomaly-engine", estado: "degraded", p95_ms: 1320 },
        { nombre: "scoring-engine", estado: "healthy", p95_ms: 260 },
      ],
    },
  };
}

// ---------------------------------------------------------------------------
// LoRa Network Intelligence — PDR, ADR, SF, batería, diversidad de gateways
// ---------------------------------------------------------------------------

export interface LoraGateway {
  gateway_id: string;
  dma_codigo: string;
  estado: "online" | "degradado" | "offline";
  rssi_dbm: number;
  snr_db: number;
  uptime_pct: number;
  pdr_pct: number;            // packet delivery ratio
  saturacion_pct: number;     // duty-cycle / canal ocupado
  medidores: number;
  uplinks_h: number;
  lon: number;
  lat: number;
}

export interface LoraDevice {
  device_id: string;
  dma_codigo: string;
  pdr_pct: number;
  rssi_dbm: number;
  snr_db: number;
  sf_actual: number;          // spreading factor vigente (7..12)
  sf_sugerido: number;        // SF recomendado por el modelo
  adr_activo: boolean;
  retries_pct: number;
  bateria_pct: number;
  bateria_meses_rest: number;
  gateways_visibles: number;  // diversidad de gateways
  lecturas_dia_pct: number;   // % de lecturas diarias completas
  riesgo: "ok" | "vigilancia" | "critico";
}

export interface LoraRecommendation {
  rec_id: string;
  tipo: "reubicar_gateway" | "ajustar_sf" | "habilitar_adr" | "reemplazar_dispositivo" | "agregar_gateway";
  titulo: string;
  detalle: string;
  impacto: string;
  prioridad: "alta" | "media" | "baja";
  ref: string;
  to: string;
}

interface LoraSnapshot {
  kpis: {
    pdr_red_pct: number;
    lecturas_completas_pct: number;
    adr_effectiveness_pct: number;
    dispositivos_total: number;
    dispositivos_criticos: number;
    bateria_critica: number;
    gateways_online: number;
    gateways_total: number;
    uplinks_h: number;
  };
  gateways: LoraGateway[];
  devices: LoraDevice[];
  recomendaciones: LoraRecommendation[];
  pdr_trend: number[];
}

function suggestSf(rssi: number, snr: number): number {
  // Heurística inspirada en la literatura (RSSI/SNR → SF óptimo):
  // mejor enlace ⇒ SF más bajo (más throughput, menos energía).
  if (snr >= 8 && rssi > -95) return 7;
  if (snr >= 5 && rssi > -105) return 8;
  if (snr >= 2 && rssi > -112) return 9;
  if (snr >= 0 && rssi > -118) return 10;
  if (rssi > -123) return 11;
  return 12;
}

function buildLoraSnapshot(): LoraSnapshot {
  const r = rng(313);
  const real = realDataSync();

  let gateways: LoraGateway[];
  if (real && real.gateways.length > 0) {
    // Gateways REALES de Antofagasta (119 totales; mostramos top 30).
    // Repartidos sobre las DMAs reales para tener coordenadas.
    const dmas = listDemoDmas();
    gateways = real.gateways.map((g, i) => {
      const dma = dmas[i % dmas.length];
      const lossPct = Math.max(0, Math.min(100, (g.avg_rssi + 130) * -1 + 30));
      const pdr = Math.max(40, 100 - (g.avg_rssi < -115 ? 18 : g.avg_rssi < -108 ? 9 : 3) - r() * 4);
      const estado: LoraGateway["estado"] =
        g.avg_rssi < -116 ? "degradado" : "online";
      return {
        gateway_id: g.gateway_id.slice(0, 12),
        dma_codigo: dma.codigo,
        estado,
        rssi_dbm: Math.round(g.avg_rssi),
        snr_db: Number(g.avg_snr.toFixed(1)),
        uptime_pct: Number((97 + r() * 2.5).toFixed(2)),
        pdr_pct: Number(pdr.toFixed(1)),
        saturacion_pct: Number(Math.min(95, g.uplinks / 2000).toFixed(1)),
        medidores: g.medidores,
        uplinks_h: Math.round(g.uplinks / (31 * 24)),
        lon: dma.lon + (r() - 0.5) * 0.02,
        lat: dma.lat + (r() - 0.5) * 0.02,
      };
      void lossPct;
    });
  } else {
    gateways = DEMO_DMAS.map((d, i) => {
      const estado: LoraGateway["estado"] = i === 2 ? "degradado" : i === 4 ? "offline" : "online";
      const pdr = estado === "offline" ? 0 : estado === "degradado" ? 72 + r() * 6 : 94 + r() * 5;
      return {
        gateway_id: `GW-${d.codigo}`,
        dma_codigo: d.codigo,
        estado,
        rssi_dbm: estado === "offline" ? -125 : Math.round(-78 - r() * 30),
        snr_db: estado === "offline" ? -8 : Number((2 + r() * 9).toFixed(1)),
        uptime_pct: estado === "offline" ? 0 : Number((96.5 + r() * 3.3).toFixed(2)),
        pdr_pct: Number(pdr.toFixed(1)),
        saturacion_pct: Number((estado === "degradado" ? 64 + r() * 22 : 18 + r() * 30).toFixed(1)),
        medidores: Math.round(300 + r() * 900),
        uplinks_h: Math.round(estado === "offline" ? 0 : 4200 + r() * 5200),
        lon: d.lon + 0.004,
        lat: d.lat + 0.004,
      };
    });
  }

  // Devices: a spread of healthy + degraded ones, with SF mismatch.
  const devices: LoraDevice[] = Array.from({ length: 16 }, (_, i) => {
    const d = DEMO_DMAS[i % DEMO_DMAS.length];
    const degraded = i % 5 === 0;
    const rssi = degraded ? Math.round(-118 - r() * 8) : Math.round(-92 - r() * 22);
    const snr = degraded ? Number((-4 + r() * 4).toFixed(1)) : Number((3 + r() * 8).toFixed(1));
    const sfActual = degraded ? 12 : [7, 8, 9, 10][Math.floor(r() * 4)];
    const sfSug = suggestSf(rssi, snr);
    const pdr = degraded ? 58 + r() * 18 : 92 + r() * 7;
    const bateria = degraded ? 8 + r() * 18 : 40 + r() * 55;
    const gwVis = degraded ? 1 : 2 + Math.floor(r() * 3);
    const riesgo: LoraDevice["riesgo"] =
      pdr < 75 || bateria < 15 || gwVis < 2 ? "critico" : pdr < 90 || bateria < 30 ? "vigilancia" : "ok";
    return {
      device_id: `MTR-${d.codigo}-${(100 + i).toString()}`,
      dma_codigo: d.codigo,
      pdr_pct: Number(pdr.toFixed(1)),
      rssi_dbm: rssi,
      snr_db: snr,
      sf_actual: sfActual,
      sf_sugerido: sfSug,
      adr_activo: !degraded,
      retries_pct: Number((degraded ? 18 + r() * 22 : r() * 8).toFixed(1)),
      bateria_pct: Number(bateria.toFixed(0)),
      bateria_meses_rest: Number((bateria / 3.2).toFixed(0)),
      gateways_visibles: gwVis,
      lecturas_dia_pct: Number((pdr - r() * 4).toFixed(1)),
      riesgo,
    };
  });

  const recomendaciones: LoraRecommendation[] = [
    {
      rec_id: "LREC-1",
      tipo: "reubicar_gateway",
      titulo: "Reubicar GW-DMA-003 (saturación 78%)",
      detalle:
        "El gateway de DMA-003 opera con duty-cycle alto y PDR cayendo a 72%. Reubicar 300m al oeste o añadir un segundo gateway reduce colisiones (LoRaWAN CSMA / TR013).",
      impacto: "+18% lecturas completas en DMA-003",
      prioridad: "alta",
      ref: "GW-DMA-003",
      to: "/observabilidad",
    },
    {
      rec_id: "LREC-2",
      tipo: "agregar_gateway",
      titulo: "Cobertura insuficiente en zona norte",
      detalle:
        "GW-DMA-005 offline deja 1 medidor sin diversidad de gateways (sin redundancia). Desplegar gateway redundante elimina puntos ciegos de lectura.",
      impacto: "Elimina 1 zona ciega · protege balance DMA-005",
      prioridad: "alta",
      ref: "GW-DMA-005",
      to: "/cobertura",
    },
    {
      rec_id: "LREC-3",
      tipo: "ajustar_sf",
      titulo: "3 dispositivos con SF subóptimo",
      detalle:
        "Medidores con buen enlace usando SF12 innecesario: bajar SF según RSSI/SNR aumenta throughput y autonomía de batería sin perder cobertura.",
      impacto: "−22% time-on-air · +autonomía batería",
      prioridad: "media",
      ref: "SF mismatch",
      to: "/lora",
    },
    {
      rec_id: "LREC-4",
      tipo: "reemplazar_dispositivo",
      titulo: "Baterías críticas (<15%)",
      detalle:
        "Dispositivos con batería bajo 15% y PDR degradado: programar reemplazo antes de pérdida total de lectura.",
      impacto: "Evita huecos de lectura en balance hídrico",
      prioridad: "media",
      ref: "batería crítica",
      to: "/lora",
    },
  ];

  const online = gateways.filter((g) => g.estado !== "offline");
  const pdrRed = online.reduce((a, g) => a + g.pdr_pct, 0) / Math.max(1, online.length);
  const lecturasCompletas =
    devices.reduce((a, d) => a + d.lecturas_dia_pct, 0) / Math.max(1, devices.length);
  const adrActivos = devices.filter((d) => d.adr_activo).length;

  // Con datos reales: total de medidores y gateways reales de Antofagasta.
  const dispositivosTotal = real ? real.executive?.medidores ?? 36660 : 9842;
  const gatewaysTotal = real ? 119 : gateways.length;
  const bateriaCritica = real ? real.executive?.battery ?? 0 : 18;

  return {
    kpis: {
      pdr_red_pct: Number(pdrRed.toFixed(1)),
      lecturas_completas_pct: Number(lecturasCompletas.toFixed(1)),
      adr_effectiveness_pct: Number(((adrActivos / devices.length) * 100).toFixed(0)),
      dispositivos_total: dispositivosTotal,
      dispositivos_criticos: devices.filter((d) => d.riesgo === "critico").length * 14,
      bateria_critica: bateriaCritica,
      gateways_online: real ? gatewaysTotal - 0 : online.length,
      gateways_total: gatewaysTotal,
      uplinks_h: gateways.reduce((a, g) => a + g.uplinks_h, 0),
    },
    gateways,
    devices,
    recomendaciones,
    pdr_trend: [97.2, 96.8, 96.1, 95.4, 94.6, Number(pdrRed.toFixed(1))],
  };
}

// ---------------------------------------------------------------------------
// Predicción ML
// ---------------------------------------------------------------------------

interface MlModels {
  modelos: Array<{
    nombre: string;
    tipo: string;
    metrica: string;
    valor: number;
    estado: string;
    confianza: number;
    drift: number;
    features: Array<{ nombre: string; importancia: number }>;
    impacto: string;
  }>;
  forecast_nrw: number[];
  forecast_demanda: number[];
}

function buildMlModels(): MlModels {
  return {
    modelos: [
      {
        nombre: "Predicción de fugas",
        tipo: "XGBoost",
        metrica: "AUC",
        valor: 0.84,
        estado: "champion",
        confianza: 0.82,
        drift: 0.04,
        features: [
          { nombre: "presión residual", importancia: 0.31 },
          { nombre: "MNF Δ", importancia: 0.24 },
          { nombre: "edad tubería", importancia: 0.18 },
          { nombre: "reincidencia", importancia: 0.15 },
          { nombre: "material", importancia: 0.12 },
        ],
        impacto: "−2.4 pp NRW proyectado",
      },
      {
        nombre: "Predicción de fraude",
        tipo: "XGBoost + reglas",
        metrica: "Precision",
        valor: 0.63,
        estado: "champion",
        confianza: 0.71,
        drift: 0.08,
        features: [
          { nombre: "patrón consumo", importancia: 0.34 },
          { nombre: "histórico reclamos", importancia: 0.22 },
          { nombre: "ratio billed/avg", importancia: 0.2 },
          { nombre: "antigüedad medidor", importancia: 0.14 },
          { nombre: "segmento", importancia: 0.1 },
        ],
        impacto: "CLP 142 M/año recuperables",
      },
      {
        nombre: "Falla de medidor",
        tipo: "Survival (Cox PH)",
        metrica: "C-index",
        valor: 0.74,
        estado: "challenger",
        confianza: 0.7,
        drift: 0.05,
        features: [
          { nombre: "edad medidor", importancia: 0.38 },
          { nombre: "volumen acumulado", importancia: 0.27 },
          { nombre: "marca/modelo", importancia: 0.2 },
          { nombre: "calidad agua", importancia: 0.15 },
        ],
        impacto: "−18% sub-registro",
      },
      {
        nombre: "Forecasting NRW",
        tipo: "Temporal Fusion Transformer",
        metrica: "MAPE",
        valor: 7.8,
        estado: "champion",
        confianza: 0.88,
        drift: 0.03,
        features: [
          { nombre: "estacionalidad", importancia: 0.3 },
          { nombre: "tendencia DMA", importancia: 0.28 },
          { nombre: "presión", importancia: 0.22 },
          { nombre: "intervenciones", importancia: 0.2 },
        ],
        impacto: "horizonte 30d",
      },
    ],
    forecast_nrw: [26.84, 26.3, 25.7, 25.1, 24.6, 24.0],
    forecast_demanda: [284, 288, 295, 301, 298, 305],
  };
}

// ---------------------------------------------------------------------------
// Impacto económico
// ---------------------------------------------------------------------------

interface EconomicImpact {
  perdida_anual_clp: number;
  recuperable_clp: number;
  recuperado_ytd_clp: number;
  costo_operativo_clp: number;
  roi_acumulado: number;
  payback_promedio_dias: number;
  por_dma: Array<{ dma_codigo: string; perdida_clp_mes: number; recuperable_clp_mes: number; roi: number }>;
  waterfall: Array<{ etapa: string; valor_clp: number }>;
}

function buildEconomicImpact(): EconomicImpact {
  const TARIFA = 950;
  const porDma = DEMO_DMAS.map((d) => {
    const perdidaMes = Math.round(d.mnf_lps * 86.4 * 30 * TARIFA);
    const recuperable = Math.round(perdidaMes * (d.nrw_pct > 30 ? 0.55 : 0.4));
    return {
      dma_codigo: d.codigo,
      perdida_clp_mes: perdidaMes,
      recuperable_clp_mes: recuperable,
      roi: Number((recuperable / 900_000).toFixed(1)),
    };
  });
  const perdidaAnual = porDma.reduce((a, b) => a + b.perdida_clp_mes, 0) * 12;
  const recuperable = porDma.reduce((a, b) => a + b.recuperable_clp_mes, 0) * 12;
  return {
    perdida_anual_clp: perdidaAnual,
    recuperable_clp: recuperable,
    recuperado_ytd_clp: Math.round(recuperable * 0.34),
    costo_operativo_clp: Math.round(recuperable * 0.22),
    roi_acumulado: 4.2,
    payback_promedio_dias: 47,
    por_dma: porDma.sort((a, b) => b.perdida_clp_mes - a.perdida_clp_mes),
    waterfall: [
      { etapa: "Pérdida total", valor_clp: perdidaAnual },
      { etapa: "No recuperable", valor_clp: -Math.round(perdidaAnual * 0.45) },
      { etapa: "Recuperable", valor_clp: recuperable },
      { etapa: "Recuperado YTD", valor_clp: Math.round(recuperable * 0.34) },
    ],
  };
}

// ---------------------------------------------------------------------------
// Programa MVP 90 días — baseline vs resultado verificable
// ---------------------------------------------------------------------------

export interface MvpKpi {
  kpi_id: string;
  nombre: string;
  categoria: "operacional" | "financiero" | "datos" | "red";
  unidad: string;
  baseline: number;
  meta: number;
  actual: number;
  /** true cuando mejorar significa bajar (NRW, MTTR, falsos positivos…). */
  menor_es_mejor: boolean;
  metodo: string;
  fuente: string;
  serie: number[];
}

interface MvpProgram {
  perimetro: string;
  dma_incluidas: string[];
  semana_actual: number;
  semanas_total: number;
  fecha_inicio: string;
  kpis: MvpKpi[];
  hitos: Array<{
    semana: string;
    titulo: string;
    entregable: string;
    estado: "completado" | "en_progreso" | "pendiente";
  }>;
}

/** % de avance hacia la meta desde el baseline (0..100, clamp). */
export function mvpProgress(k: MvpKpi): number {
  const span = k.meta - k.baseline;
  if (span === 0) return 100;
  const adv = (k.actual - k.baseline) / span;
  return Math.max(0, Math.min(100, Math.round(adv * 100)));
}

function buildMvpProgram(): MvpProgram {
  const kpis: MvpKpi[] = [
    {
      kpi_id: "nrw_perimetro",
      nombre: "NRW del perímetro piloto",
      categoria: "operacional",
      unidad: "%",
      baseline: 34.0,
      meta: 27.0,
      actual: 30.4,
      menor_es_mejor: true,
      metodo: "Balance hídrico por DMA, snapshot semanal versionado",
      fuente: "Balance & NRW",
      serie: [34.0, 33.6, 32.9, 32.1, 31.2, 30.4],
    },
    {
      kpi_id: "hit_rate",
      nombre: "Hit-rate de inspecciones",
      categoria: "operacional",
      unidad: "%",
      baseline: 38,
      meta: 65,
      actual: 57,
      menor_es_mejor: false,
      metodo: "% órdenes cerradas con hallazgo confirmado / total",
      fuente: "Órdenes + Casos",
      serie: [38, 42, 47, 51, 54, 57],
    },
    {
      kpi_id: "agua_recuperable",
      nombre: "Agua recuperable identificada",
      categoria: "financiero",
      unidad: "m³/día",
      baseline: 0,
      meta: 1200,
      actual: 940,
      menor_es_mejor: false,
      metodo: "Σ pérdida estimada de casos abiertos × prob. recuperación",
      fuente: "Casos Prioritarios",
      serie: [0, 280, 470, 660, 820, 940],
    },
    {
      kpi_id: "ingreso_recuperable",
      nombre: "Ingreso recuperable identificado",
      categoria: "financiero",
      unidad: "CLP/mes",
      baseline: 0,
      meta: 32_000_000,
      actual: 26_790_000,
      menor_es_mejor: false,
      metodo: "Agua recuperable × tarifa promedio",
      fuente: "Impacto Económico",
      serie: [0, 7_900_000, 13_400_000, 18_800_000, 23_400_000, 26_790_000],
    },
    {
      kpi_id: "falsos_positivos",
      nombre: "Falsos positivos en alarmas",
      categoria: "operacional",
      unidad: "%",
      baseline: 46,
      meta: 18,
      actual: 24,
      menor_es_mejor: true,
      metodo: "% alertas descartadas tras inspección / total inspeccionadas",
      fuente: "Centro de Alertas",
      serie: [46, 41, 36, 31, 27, 24],
    },
    {
      kpi_id: "latencia_datos",
      nombre: "Latencia de datos (ingesta→tablero)",
      categoria: "datos",
      unidad: "s",
      baseline: 1800,
      meta: 60,
      actual: 4.2,
      menor_es_mejor: true,
      metodo: "p95 desde uplink a disponibilidad en dashboard",
      fuente: "Observabilidad",
      serie: [1800, 900, 420, 180, 45, 4.2],
    },
    {
      kpi_id: "fuentes_integradas",
      nombre: "Fuentes integradas",
      categoria: "datos",
      unidad: "de 8",
      baseline: 0,
      meta: 8,
      actual: 6,
      menor_es_mejor: false,
      metodo: "Conectores OT/IT/LoRa con data contract activo",
      fuente: "Integraciones",
      serie: [0, 1, 2, 4, 5, 6],
    },
    {
      kpi_id: "pdr_red",
      nombre: "Lecturas completas (PDR)",
      categoria: "red",
      unidad: "%",
      baseline: 88.5,
      meta: 97.0,
      actual: 94.6,
      menor_es_mejor: false,
      metodo: "Packet delivery ratio promedio de la red LoRa",
      fuente: "LoRa Network",
      serie: [88.5, 90.1, 91.8, 93.0, 94.0, 94.6],
    },
    {
      kpi_id: "mttr",
      nombre: "MTTR de órdenes (cierre)",
      categoria: "operacional",
      unidad: "h",
      baseline: 96,
      meta: 36,
      actual: 48,
      menor_es_mejor: true,
      metodo: "Tiempo medio asignación → cierre confirmado",
      fuente: "SLA",
      serie: [96, 84, 72, 60, 52, 48],
    },
  ];

  return {
    perimetro: "3 DMAs · 8.430 conexiones",
    dma_incluidas: ["DMA-001", "DMA-003", "DMA-005"],
    semana_actual: 10,
    semanas_total: 12,
    fecha_inicio: "2025-03-24",
    kpis,
    hitos: [
      { semana: "1–2", titulo: "Descubrimiento + baseline", entregable: "Diseño y KPI baseline", estado: "completado" },
      { semana: "3–4", titulo: "Conectores OT/IT/LoRa", entregable: "Fuentes conectadas + data contracts", estado: "completado" },
      { semana: "5–6", titulo: "Modelo semántico + lakehouse", entregable: "Vista unificada operacional", estado: "completado" },
      { semana: "7–8", titulo: "Scoring + balance DMA", entregable: "Backlog priorizado de acciones", estado: "completado" },
      { semana: "9–10", titulo: "Modelos ML + validación", entregable: "Ranking fugas/ilícitos/fallas", estado: "en_progreso" },
      { semana: "11–12", titulo: "Cierre piloto + business case", entregable: "Informe ejecutivo y plan de escala", estado: "pendiente" },
    ],
  };
}

// ---------------------------------------------------------------------------
// Operational Insights — conclusiones automáticas con impacto + recomendación
// ---------------------------------------------------------------------------

export interface OperationalInsight {
  insight_id: string;
  categoria: "fuga" | "fraude" | "infraestructura" | "operacion" | "calidad_datos";
  severidad: AlertaSeveridad;
  titulo: string;
  detalle: string;
  impacto_clp_mes: number | null;
  impacto_m3_anio: number | null;
  confianza: number;
  recomendacion: string;
  entidades: Array<{ tipo: string; ref: string; to: string }>;
  detectado_en: string;
  tendencia: number[];
}

function buildInsights(): OperationalInsight[] {
  const now = new Date("2025-06-04T12:00:00Z").getTime();
  const mk = (mins: number) => new Date(now - mins * 60_000).toISOString();
  return [
    {
      insight_id: "INS-1001",
      categoria: "fuga",
      severidad: "critica",
      titulo: "DMA-003 incrementó MNF +18% en 72 h",
      detalle:
        "El caudal mínimo nocturno de DMA-003 subió de 6.6 a 7.8 L/s en 72 h sin aumento de demanda diurna. Patrón típico de fuga no visible en matriz.",
      impacto_clp_mes: 2_100_000,
      impacto_m3_anio: 26_500,
      confianza: 0.87,
      recomendacion:
        "Programar step-test nocturno y barrido acústico en el sub-sector aguas arriba de la válvula V-12.",
      entidades: [
        { tipo: "DMA", ref: "DMA-003", to: "/mapa?dma=DMA-003" },
        { tipo: "Caso", ref: "Casos Prioritarios", to: "/casos" },
      ],
      detectado_en: mk(35),
      tendencia: [6.6, 6.7, 6.9, 7.1, 7.4, 7.8],
    },
    {
      insight_id: "INS-1002",
      categoria: "fraude",
      severidad: "alta",
      titulo: "14 medidores con patrón probable de subregistro",
      detalle:
        "Cluster de 14 medidores residenciales en DMA-005 muestra consumo plano 40% bajo el esperado para su segmento, compatible con subregistro o bypass.",
      impacto_clp_mes: 980_000,
      impacto_m3_anio: 8_200,
      confianza: 0.74,
      recomendacion:
        "Inspección encubierta de los 14 empalmes; priorizar los 5 con mayor divergencia histórica.",
      entidades: [
        { tipo: "DMA", ref: "DMA-005", to: "/mapa?dma=DMA-005" },
        { tipo: "Predicción", ref: "Modelo fraude", to: "/prediccion" },
      ],
      detectado_en: mk(82),
      tendencia: [12, 12, 13, 13, 14, 14],
    },
    {
      insight_id: "INS-1003",
      categoria: "infraestructura",
      severidad: "alta",
      titulo: "Cobertura RF degradada en zona norte",
      detalle:
        "3 gateways LoRaWAN de la zona norte presentan pérdida de paquetes > 22%. Riesgo de huecos de lectura que distorsionan el balance de DMA-002.",
      impacto_clp_mes: null,
      impacto_m3_anio: null,
      confianza: 0.92,
      recomendacion:
        "Revisar alimentación y antena de GW-DMA-002; reubicar o añadir gateway redundante.",
      entidades: [
        { tipo: "Observabilidad", ref: "Gateways", to: "/observabilidad" },
        { tipo: "Cobertura", ref: "Cobertura IoT", to: "/cobertura" },
      ],
      detectado_en: mk(120),
      tendencia: [8, 11, 14, 18, 21, 22],
    },
    {
      insight_id: "INS-1004",
      categoria: "operacion",
      severidad: "media",
      titulo: "Cuadrilla 4 con SLA bajo objetivo",
      detalle:
        "La Cuadrilla Fraude & medición presenta MTTR +31% sobre objetivo en los últimos 14 días. Posible sobrecarga o falta de skills para casos asignados.",
      impacto_clp_mes: null,
      impacto_m3_anio: null,
      confianza: 0.81,
      recomendacion:
        "Rebalancear la cola: reasignar 3 casos P3 a Cuadrilla Norte (disponible).",
      entidades: [
        { tipo: "Cuadrilla", ref: "Cuadrillas", to: "/cuadrillas" },
        { tipo: "SLA", ref: "Tablero SLA", to: "/sla" },
      ],
      detectado_en: mk(190),
      tendencia: [30, 32, 35, 36, 38, 39],
    },
    {
      insight_id: "INS-1005",
      categoria: "fuga",
      severidad: "alta",
      titulo: "DMA-005 proyecta superar umbral NRW en 3 semanas",
      detalle:
        "El forecast TFT proyecta que DMA-005 cruzará 33% de NRW en ~21 días si la tendencia se mantiene. Intervención temprana reduce pérdida acumulada.",
      impacto_clp_mes: 1_450_000,
      impacto_m3_anio: 18_300,
      confianza: 0.83,
      recomendacion:
        "Adelantar campaña de detección en DMA-005 antes del cruce de umbral.",
      entidades: [
        { tipo: "DMA", ref: "DMA-005", to: "/mapa?dma=DMA-005" },
        { tipo: "Predicción", ref: "Forecast NRW", to: "/prediccion" },
      ],
      detectado_en: mk(240),
      tendencia: [28.4, 29.6, 30.5, 31.4, 32.2, 33.0],
    },
    {
      insight_id: "INS-1006",
      categoria: "calidad_datos",
      severidad: "media",
      titulo: "41 gaps temporales detectados en 24 h",
      detalle:
        "Aumento de huecos en series de lectura, concentrados en gateways con SNR bajo. Riesgo de balance hídrico subestimado en DMA-002 y DMA-005.",
      impacto_clp_mes: null,
      impacto_m3_anio: null,
      confianza: 0.78,
      recomendacion:
        "Activar interpolación etiquetada y priorizar reparación de cobertura RF norte.",
      entidades: [
        { tipo: "Calidad", ref: "Calidad de Datos", to: "/calidad" },
      ],
      detectado_en: mk(300),
      tendencia: [22, 28, 33, 36, 39, 41],
    },
  ];
}

// ---------------------------------------------------------------------------
// Live Operations — feed de eventos en tiempo real (event-sourced)
// ---------------------------------------------------------------------------

export interface LiveEvent {
  event_id: string;
  ts: string;
  tipo:
    | "alerta_creada"
    | "orden_asignada"
    | "inspeccion_iniciada"
    | "fuga_confirmada"
    | "evidencia_subida"
    | "sla_vencido"
    | "gateway_offline"
    | "dma_degradado"
    | "medidor_reemplazado"
    | "lectura_batch";
  severidad: AlertaSeveridad;
  mensaje: string;
  ref: string | null;
  to: string | null;
}

const LIVE_TEMPLATES: Array<Omit<LiveEvent, "event_id" | "ts">> = [
  { tipo: "alerta_creada", severidad: "critica", mensaje: "Nueva alerta crítica: fuga probable en DMA-003", ref: "DMA-003", to: "/alertas" },
  { tipo: "gateway_offline", severidad: "alta", mensaje: "Gateway GW-DMA-005 sin reportar hace 6 min", ref: "GW-DMA-005", to: "/observabilidad" },
  { tipo: "orden_asignada", severidad: "media", mensaje: "Orden OR-912 asignada a Cuadrilla Norte", ref: "OR-912", to: "/inspector/ordenes" },
  { tipo: "fuga_confirmada", severidad: "alta", mensaje: "Fuga confirmada en eje Sur — 142 m³/día recuperables", ref: "CASE-1003", to: "/casos" },
  { tipo: "dma_degradado", severidad: "alta", mensaje: "DMA-005 cruzó umbral de vigilancia (NRW 31.4%)", ref: "DMA-005", to: "/mapa?dma=DMA-005" },
  { tipo: "inspeccion_iniciada", severidad: "baja", mensaje: "Inspector Soto inició inspección en CLI-2007", ref: "CLI-2007", to: "/inspector/ordenes" },
  { tipo: "evidencia_subida", severidad: "baja", mensaje: "Evidencia fotográfica subida para OR-908", ref: "OR-908", to: "/evidencias" },
  { tipo: "sla_vencido", severidad: "critica", mensaje: "SLA vencido en orden OR-901 (P1)", ref: "OR-901", to: "/sla" },
  { tipo: "lectura_batch", severidad: "baja", mensaje: "Batch de 12.480 lecturas ingerido (lag 3.4s)", ref: null, to: "/observabilidad" },
  { tipo: "medidor_reemplazado", severidad: "media", mensaje: "Medidor M-DMA-001-7 reemplazado por subregistro", ref: "M-DMA-001-7", to: "/supervisor/catastro" },
];

function buildLiveEvents(seedOffsetSec: number): LiveEvent[] {
  // Deterministic-but-rolling feed: the offset (driven by the client
  // clock) shifts which templates appear "now", simulating streaming.
  const now = Date.now();
  const out: LiveEvent[] = [];
  for (let i = 0; i < 18; i += 1) {
    const tpl = LIVE_TEMPLATES[(i + seedOffsetSec) % LIVE_TEMPLATES.length];
    out.push({
      ...tpl,
      event_id: `EVT-${now}-${i}`,
      ts: new Date(now - i * 17_000 - (seedOffsetSec % 17) * 1000).toISOString(),
    });
  }
  return out;
}

interface LiveSnapshot {
  events: LiveEvent[];
  metrics: {
    ingest_rate_h: number;
    pipeline_lag_s: number;
    alertas_activas: number;
    gateways_online: number;
    gateways_total: number;
    cuadrillas_activas: number;
    sla_en_riesgo: number;
    api_p95_ms: number;
    presion_media_bar: number;
  };
}

function buildLiveSnapshot(): LiveSnapshot {
  const seed = Math.floor(Date.now() / 4000);
  const jitter = (seed % 7) - 3;
  return {
    events: buildLiveEvents(seed),
    metrics: {
      ingest_rate_h: 1_240_000 + jitter * 9000,
      pipeline_lag_s: Number((3.4 + (seed % 5) * 0.2).toFixed(1)),
      alertas_activas: ALERTAS_DEMO.filter((a) => a.estado !== "cerrada").length,
      gateways_online: 4,
      gateways_total: 5,
      cuadrillas_activas: 3,
      sla_en_riesgo: 6,
      api_p95_ms: 180 + jitter * 6,
      presion_media_bar: Number((3.7 + (seed % 4) * 0.05).toFixed(2)),
    },
  };
}

// ---------------------------------------------------------------------------
// ANC Copilot — asistente contextual (respuestas canned en modo demo)
// ---------------------------------------------------------------------------

interface CopilotResponse {
  answer: string;
  bullets: string[];
  refs: Array<{ label: string; to: string }>;
}

function buildCopilotResponse(question: string): CopilotResponse {
  const q = question.toLowerCase();

  if (q.includes("dma-003") || (q.includes("nrw") && q.includes("aument"))) {
    return {
      answer:
        "El NRW de DMA-003 subió principalmente por una fuga no visible en matriz. El MNF aumentó +18% en 72h sin cambio en la demanda diurna, lo que descarta mayor consumo facturado.",
      bullets: [
        "MNF pasó de 6.6 a 7.8 L/s en 72h (confianza 87%).",
        "Impacto estimado: CLP 2.1M/mes · ~26.500 m³/año.",
        "Sin correlación con eventos de presión → fuga estructural probable.",
        "Recomendación: step-test nocturno + barrido acústico aguas arriba de V-12.",
      ],
      refs: [
        { label: "Ver DMA-003 en mapa", to: "/mapa?dma=DMA-003" },
        { label: "Caso prioritario asociado", to: "/casos" },
      ],
    };
  }

  if (q.includes("roi") || q.includes("mayor") && q.includes("caso")) {
    return {
      answer:
        "Los casos con mayor ROI hoy combinan alta pérdida recuperable y bajo costo de intervención. El ranking de Casos Prioritarios ya los ordena por Operational Priority Score.",
      bullets: [
        "Top ROI: fugas troncales en DMA-003 y DMA-005 (×4–6).",
        "Payback medio de los P1: 47 días.",
        "Recuperable estimado del top 5: ~CLP 6.2M/mes.",
        "Sugerencia: despachar el top 5 en bloque para amortizar traslados.",
      ],
      refs: [
        { label: "Abrir Casos Prioritarios", to: "/casos" },
        { label: "Impacto Económico", to: "/impacto" },
      ],
    };
  }

  if (q.includes("medidor") && (q.includes("fall") || q.includes("reempl"))) {
    return {
      answer:
        "El modelo de supervivencia (Cox PH, C-index 0.74) identifica los medidores con mayor probabilidad de falla este mes por edad, volumen acumulado y lecturas erráticas.",
      bullets: [
        "~38 medidores en riesgo alto este mes.",
        "Drivers principales: antigüedad (38%) y volumen acumulado (27%).",
        "Reemplazo preventivo evita ~18% de subregistro en esos puntos.",
        "Generar órdenes de reemplazo priorizadas por DMA con mayor NRW.",
      ],
      refs: [
        { label: "Ver modelos de Predicción", to: "/prediccion" },
        { label: "Casos de falla de medidor", to: "/casos" },
      ],
    };
  }

  if (q.includes("alerta") && (q.includes("critic") || q.includes("resum"))) {
    return {
      answer:
        "Hay alertas críticas activas concentradas en DMA-003 y DMA-005. Las más urgentes son fugas probables y un medidor manipulado con SLA de 8h.",
      bullets: [
        "9 alertas abiertas, varias críticas con SLA 8h.",
        "Correlación geo-temporal: incidente de fuga en zona norte agrupa 4 alertas.",
        "Mayor impacto económico: fuga troncal DMA-003.",
        "Acción inmediata: asignar las P1 a cuadrillas disponibles.",
      ],
      refs: [
        { label: "Abrir Centro de Alertas", to: "/alertas" },
        { label: "Ver en mapa", to: "/mapa" },
      ],
    };
  }

  if (q.includes("resum") && q.includes("dma")) {
    return {
      answer:
        "Resumen de red: NRW global 26.84% (−1.6pp). DMA-003 y DMA-005 son las de peor desempeño y concentran el mayor potencial de recuperación.",
      bullets: [
        "DMA-003: NRW 36.1% · ILI 2.9 · MNF 7.8 L/s → crítica.",
        "DMA-005: NRW 31.4% · tendencia al alza → vigilancia.",
        "DMA-002 y DMA-004: estables, NRW < 23%.",
        "Foco recomendado: DMA-003 (mayor impacto absoluto).",
      ],
      refs: [
        { label: "Ver DMAs", to: "/dmas" },
        { label: "Mapa operacional", to: "/mapa" },
      ],
    };
  }

  // Fallback genérico.
  return {
    answer:
      "Puedo ayudarte a interpretar la operación: anomalías por DMA, casos de mayor ROI, medidores en riesgo, resumen de alertas críticas y tendencias de NRW. Prueba con una de las preguntas sugeridas.",
    bullets: [
      "Ejemplo: \"¿Por qué aumentó el NRW en DMA-003?\"",
      "Ejemplo: \"¿Qué casos tienen mayor ROI?\"",
      "Ejemplo: \"¿Qué medidores probablemente fallarán este mes?\"",
      "Ejemplo: \"Resume las alertas críticas.\"",
    ],
    refs: [
      { label: "Operational Insights", to: "/insights" },
      { label: "Casos Prioritarios", to: "/casos" },
    ],
  };
}

// ---------------------------------------------------------------------------
// Mock router
// ---------------------------------------------------------------------------

export async function mockApi<T>(
  path: string,
  _init: ApiRequestInit,
): Promise<T> {
  // Carga (una vez) los datos reales de Antofagasta si están disponibles.
  // Si existen, los builders los prefieren automáticamente.
  await loadRealData();

  const [route, query = ""] = path.split("?", 2);
  const params = new URLSearchParams(query);

  if (/^\/dashboards\/dma\/[^/]+\/consumo$/.test(route)) {
    const dmaId = route.split("/")[3];
    const periodoId = params.get("periodo_id") ?? "2025-01";
    return buildDashboard(dmaId, periodoId) as unknown as T;
  }

  if (route === "/nrw") {
    const periodoId = params.get("periodo_id") ?? "2025-05";
    return buildNrwList(periodoId) as unknown as T;
  }

  if (route === "/clientes") return buildClientes() as unknown as T;
  if (route === "/ordenes/mias") return buildOrdenes() as unknown as T;
  if (route === "/ordenes") return buildOrdenes() as unknown as T;

  if (route === "/alertas") {
    const sev = params.get("severidad");
    const estado = params.get("estado");
    const q = params.get("q")?.toLowerCase();
    let out = [...ALERTAS_DEMO];
    if (sev) out = out.filter((a) => a.severidad === sev);
    if (estado) out = out.filter((a) => a.estado === estado);
    if (q) {
      out = out.filter(
        (a) =>
          a.cliente_codigo.toLowerCase().includes(q) ||
          a.tipo_label.toLowerCase().includes(q) ||
          a.dma_codigo.toLowerCase().includes(q),
      );
    }
    out.sort((a, b) => b.score - a.score);
    return out as unknown as T;
  }

  if (/^\/alertas\/[^/]+$/.test(route)) {
    const id = route.split("/")[2];
    const found = ALERTAS_DEMO.find((a) => a.alerta_id === id);
    return (found ?? null) as unknown as T;
  }

  if (route === "/executive/kpis") {
    return buildExecutiveKpis() as unknown as T;
  }

  if (route === "/gis/features") {
    return buildGisFeatures() as unknown as T;
  }

  if (route === "/quality/metrics") {
    return buildDqMetrics() as unknown as T;
  }

  if (route === "/casos") {
    const prioridad = params.get("prioridad");
    const estado = params.get("estado");
    let out = [...CASOS_DEMO];
    if (prioridad) out = out.filter((c) => c.prioridad === prioridad);
    if (estado) out = out.filter((c) => c.estado === estado);
    return out as unknown as T;
  }

  if (/^\/casos\/[^/]+$/.test(route)) {
    const id = route.split("/")[2];
    return (CASOS_DEMO.find((c) => c.caso_id === id) ?? null) as unknown as T;
  }

  if (route === "/cuadrillas") return buildCuadrillas() as unknown as T;
  if (route === "/observability") return buildObservability() as unknown as T;
  if (route === "/lora") return buildLoraSnapshot() as unknown as T;
  if (route === "/ml/models") return buildMlModels() as unknown as T;
  if (route === "/economic/impact") return buildEconomicImpact() as unknown as T;
  if (route === "/mvp/program") return buildMvpProgram() as unknown as T;
  if (route === "/insights") return buildInsights() as unknown as T;
  if (route === "/live") return buildLiveSnapshot() as unknown as T;

  if (route === "/copilot/ask") {
    let question = "";
    try {
      const body = _init.body ? JSON.parse(String(_init.body)) : {};
      question = String(body.question ?? "");
    } catch {
      question = "";
    }
    return buildCopilotResponse(question) as unknown as T;
  }

  if (route.endsWith("s") || route.includes("/list")) {
    return [] as unknown as T;
  }
  return {} as T;
}
